import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL, FONT_STACK_SUISSE_WORKS } from '~/components/brand/brandTokens';

export type SectionHeadingProps = {
  eyebrow: string;
  align?: 'left' | 'center';
  action?: { label: string; onClick: () => void };
  title?: string;
  description?: string;
};

/**
 * The small-caps eyebrow line Business Pulse uses to label a section, with an optional
 * right-aligned Ocean Teal action link, and an optional Suisse Works section title with a
 * one-line description beneath it. Omitting `title`/`description` renders exactly the single-line
 * form Business Pulse's two home sections use today (CatalogueList.tsx:24-43).
 */
export default function SectionHeading({ action, align = 'left', description, eyebrow, title }: SectionHeadingProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent={action ? 'space-between' : align === 'center' ? 'center' : 'space-between'}
        spacing={2}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: FONT_STACK_SUISSE_INTL,
            fontSize: 11,
            fontWeight: 650,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            textAlign: align,
          }}
        >
          {eyebrow}
        </Typography>
        {action ? (
          <ButtonBase
            onClick={action.onClick}
            sx={{
              color: tokens.accent,
              fontSize: 12.5,
              fontWeight: 650,
              px: 0.5,
              py: 0.4,
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: 2 },
            }}
          >
            {action.label}
            <ChevronRightIcon aria-hidden sx={{ fontSize: 17, ml: 0.25 }} />
          </ButtonBase>
        ) : null}
      </Stack>
      {title ? (
        <Typography
          component="h3"
          sx={{
            fontFamily: FONT_STACK_SUISSE_WORKS,
            fontSize: 24,
            letterSpacing: '-.02em',
            lineHeight: 1.15,
            mt: 1,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {description ? (
        <Typography color="text.secondary" sx={{ fontSize: 13.5, lineHeight: 1.45, mt: title ? 0.5 : 0.75 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}
